import { DynamicStandaloneArticle } from '@/components/Article';
import { StandaloneComponent, StandaloneComponentProps } from '@/types/component';
import { Content, Data, Meta } from '@/types/content';
import { SecondNativeArticleCampaign } from '@/types/secondNativeArticleCampaign';
import { useAppState } from 'lib/hooks/useAppState';
import { useIntersectionObserver } from 'lib/hooks/useIntersectionObserver';
import { useSeenArticleCampaignsLocalStorage } from 'lib/hooks/useSeenArticleCampaignsLocalStorage';
import { useRouter } from 'next/router';
import { useCallback, useRef, useState } from 'react';
import { updateAllerGaData } from './helpers';

const registerPageView = (allerGAData: Record<string, string>, trackingData: Record<string, string>) => {
  updateAllerGaData(allerGAData, trackingData, true);
  window.cts?.send?.pageview?.();
};

export interface StandaloneSecondNativeArticle extends StandaloneComponentProps {
  meta: Meta;
  data: Data;
  descendants: Content[];
  campaign: SecondNativeArticleCampaign;
}
export const StandaloneSecondNativeArticle: StandaloneComponent<StandaloneSecondNativeArticle> = ({
  meta,
  data,
  descendants,
  campaign,
}) => {
  const isCampaignRegistered = useRef(false);
  const [_, registerCampaignView] = useSeenArticleCampaignsLocalStorage();
  const [firstArticleTrackingData, setFirstArticleTrackingData] = useState<{
    trackingData: Record<string, string> | null;
    allerGaData: Record<string, string> | null;
  }>({
    trackingData: null,
    allerGaData: null,
  });
  const [{ publishedUrl }] = useAppState();
  const articleRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const callback: IntersectionObserverCallback = useCallback(
    (entries) => {
      const { analyticsData } = data ?? {};
      const { allerGaData, trackingData } = analyticsData ?? {};

      const getSecondNativeUrl = () => {
        const searchParams = new URLSearchParams({
          second_article: String(true),
        }).toString();

        return data.publishedUrl + '?' + searchParams;
      };

      entries.forEach(async (entry) => {
        const isArticleVisible = entry.isIntersecting;
        const isArticleFullyRead = entry.boundingClientRect.y < 0;
        const isArticleActive = isArticleVisible || isArticleFullyRead;
        const isPlayerFloating = window.jwplayer?.()?.getFloating();

        const resolvedUrl = isArticleActive ? getSecondNativeUrl() : publishedUrl;

        if (isArticleVisible && isPlayerFloating) {
          window.jwplayer?.()?.setFloating(false);
        }

        if (resolvedUrl && router.asPath !== resolvedUrl) {
          await router.push(resolvedUrl, undefined, { shallow: true, scroll: false });
        }

        if (isArticleActive && !isCampaignRegistered.current) {
          isCampaignRegistered.current = true;
          setFirstArticleTrackingData({
            allerGaData: window.aller_ga || null,
            trackingData: window.cts?.metadata || null,
          });
          registerCampaignView(campaign.campaignId);
          registerPageView(allerGaData, trackingData);
        }

        if (isCampaignRegistered.current) {
          const updatedData = isArticleActive ? { allerGaData, trackingData } : firstArticleTrackingData;
          updateAllerGaData(updatedData.allerGaData, updatedData.trackingData, isArticleActive);
        }
      });
    },
    [
      publishedUrl,
      router,
      campaign.campaignId,
      registerCampaignView,
      isCampaignRegistered,
      firstArticleTrackingData,
      data,
    ]
  );

  useIntersectionObserver([articleRef], callback, { threshold: [0] });

  const nativeAdLabel = meta?.nativeAdLabel || '';
  const affiliateLabel = data?.affiliateLabel || '';

  const disclosureLabels = [];

  if (meta?.isNativeAd && nativeAdLabel) disclosureLabels.push(nativeAdLabel);
  if (data?.hasAffiliate && affiliateLabel) disclosureLabels.push(affiliateLabel);

  return (
    <div ref={articleRef}>
      <DynamicStandaloneArticle
        articleDisclosureLabels={disclosureLabels}
        descendants={descendants}
        options={{ 'data-cts-scroll-depth-item': 'second-native-article' }}
        hide={{
          trendingMenu: true,
          breakingNews: true,
          recommendedArticles: true,
          badge: true,
        }}
      />
    </div>
  );
};

export default StandaloneSecondNativeArticle;
